import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { safeRest, isBrowser, uniqueId, clsx } from '@wds/utils';
import { PLACEMENTS } from '@wds/helpers';

import { useTheme } from 'react-jss';

import useStyles from './useStyles';

const Tooltip = ({
  delay,
  hideCaret,
  placement,
  text,
  show: propShow,
  disableHover,
  renderTo,
  onChange,
  alwaysShow,
  children,
  ...rest
}) => {
  // states
  const [show, setShow] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [stretched, setStretched] = useState(false);

  // useTheme and Styles
  const theme = useTheme();
  const classes = useStyles({
    theme,
    show: propShow || show || alwaysShow,
    placement,
    disableHover,
    hideCaret,
    stretched,
    delay,
  });

  const tooltipBodyClasses = clsx(
    classes.wrenchTooltipBody,
    classes.wrenchTooltipBodyCaret
  );

  // Refs
  const tooltipRef = useRef(null);
  const tooltipContentRef = useRef(null);

  const uniqueTooltipName = useMemo(() => uniqueId('wds-tooltip-'), []);

  // Set to 800 by default
  let TOOLTIP_TIMEOUT;

  const updatePosition = () => {
    if (
      tooltipRef.current &&
      show &&
      tooltipContentRef.current.children &&
      tooltipContentRef.current.children.length
    ) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const childRect =
        tooltipContentRef.current.children[0].getBoundingClientRect();

      const bottom = 'auto';
      const pipSize =
        placement === 'top'
          ? theme.tooltip.pipSize - theme.spacing[1]
          : theme.tooltip.pipSize + theme.spacing[1];

      const screenPlacement =
        window.innerWidth <= parseInt(theme.breakpoints.md, 10) &&
        (placement === PLACEMENTS[2] || placement === PLACEMENTS[3])
          ? PLACEMENTS[0]
          : placement;

      const position = {
        top: {
          top: rect.top - childRect.height - rect.height / 2 - pipSize,
          left: rect.left + rect.width / 2,
        },
        left: {
          top: rect.top + rect.height / 2,
          left: rect.left - childRect.width * 2,
        },
        right: {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width,
        },
      }[screenPlacement] || {
        top: rect.bottom,
        left: rect.left + rect.width / 2,
      };

      setTooltipStyle({
        top: position.top,
        left: position.left,
        zIndex: theme.zIndex.space + 1,
        position: 'fixed',
        bottom,
      });
    }
  };

  const change = (value) => {
    if (renderTo && value) {
      updatePosition();
    }

    if (onChange) {
      onChange(value);
    }
  };

  const showTooltip = (val) => {
    setShow(val);
    change(val);
  };

  const handleScroll = () => {
    if (show && renderTo) {
      updatePosition();
    }
  };

  const handleClick = (e) => {
    if (!alwaysShow) {
      if (children.props.onClick) children.props.onClick(e);
      showTooltip(!show);
    }
  };

  // componentDidMount
  useEffect(() => {
    if (show && renderTo) {
      updatePosition();
    }
    if (disableHover && isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    }

    // componentWillUnmount
    return () => {
      if (disableHover && renderTo && isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderTo, show]);

  useEffect(() => {
    if (isBrowser && renderTo && show) {
      updatePosition();
    }
  }, [show]);

  const onMouseEnter = () => {
    if (!disableHover && !alwaysShow) {
      if (delay) {
        TOOLTIP_TIMEOUT = setTimeout(() => {
          setShow(true);
          change(true);
        }, 800);
        return;
      }
      setShow(true);
      change(true);
    }
  };

  const onMouseLeave = () => {
    if (delay) {
      clearTimeout(TOOLTIP_TIMEOUT);
    }
    if (!disableHover && !alwaysShow) {
      setShow(false);
      change(false);
    }
  };

  const renderTooltipContent = () => {
    const tooltipContent = (
      <span
        className={classes.wrenchTooltip}
        id={uniqueTooltipName}
        ref={tooltipContentRef}
        role="tooltip"
        style={tooltipStyle}
      >
        <span className={tooltipBodyClasses}>{text}</span>
      </span>
    );

    if (renderTo) {
      return createPortal(tooltipContent, renderTo);
    }

    return tooltipContent;
  };

  const newChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    const { type, props: childProps } = child;

    if (childProps && childProps.stretched !== stretched) {
      setStretched(childProps.stretched);
    }

    const { displayName } = type;
    const additionalProps = {
      ...(displayName && displayName !== 'Button' && { tabIndex: 0 }),
      ...(displayName
        ? { describedby: uniqueTooltipName }
        : { 'aria-describedby': uniqueTooltipName }),
      onClick:
        !disableHover && childProps.onClick ? childProps.onClick : handleClick,
    };
    return React.cloneElement(child, { ...additionalProps });
  });

  return (
    <span className={classes.wrenchTooltipContainer} {...safeRest(rest)}>
      {renderTooltipContent()}
      <span
        className={classes.wrenchTooltipTrigger}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onMouseEnter}
        onBlur={onMouseLeave}
        ref={tooltipRef}
      >
        {newChildren}
      </span>
    </span>
  );
};

Tooltip.propTypes = {
  /**
   * Tooltip wraps around any component that requires it
   */
  children: PropTypes.node,
  /**
   * Add a short entrance delay before summoning the tooltip when you hover a trigger. The delay is 1s.
   */
  delay: PropTypes.bool,
  /**
   * You can remove the caret from the tooltip for instances
   * when showing context of which object summoned it is not necessary.
   */
  hideCaret: PropTypes.bool,
  /**
   * Placement variants, they will adjust according to screen size.
   * Left & Right placement will be adjusted to top or bottom if your screen size cannot
   * adequately display them
   */
  placement: PropTypes.oneOf(PLACEMENTS),
  /**
   * The content for the tooltip
   */
  text: PropTypes.node.isRequired,
  /**
   * If true, Tooltip will display in current view
   */
  show: PropTypes.bool,
  /**
   * If true, tooltip must be triggered by click handler.
   * Hovering over the child component will no longer work
   */
  disableHover: PropTypes.bool,
  /**
   * If your tooltip is within a container which hides overflowing content such as a modal or card,
   * you may want to render the tooltip attached to a parent element so that it can be rendered outside of
   * the trigger container. The tooltip component will handle positioning the popout relative to the trigger element.
   */
  renderTo: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
  /**
   * onChange handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,
  /**
   * If true, always display the tooltip
   */
  alwaysShow: PropTypes.bool,
};

Tooltip.defaultProps = {
  children: undefined,
  delay: false,
  hideCaret: false,
  placement: 'top',
  show: false,
  disableHover: false,
  renderTo: undefined,
  onChange: () => undefined,
  alwaysShow: undefined,
};

export default Tooltip;
